import { useCallback, useEffect, useRef, useState } from 'react';
import { getLaunchHistory } from '@repo/front-end-services';
import type { TwinLaunchHistoryResponse } from '@repo/frontend-types';

/**
 * The twin's launch history (`twin-launch-history-panel`, R1/R6). Read at page level whenever the
 * twin is known (`twin-details-page-landing`, R4), because it also decides the wizard's route —
 * a live twin with no completed launch is on its *First launch* — and so must be read whichever step
 * the wizard opens on. Read again whenever `readKey` changes: the page bumps it on arrival at Twin
 * Details, so a launch made on the Launch step is in the panel on return, and when a launch
 * completes, so the route follows.
 *
 * A failed read is `failed` — never an empty history, which would claim the twin was never
 * launched. So is a read that cannot be made (no token or twin): it would otherwise sit at
 * `loading` for ever. A change of twin, or leaving the step, bumps a generation, so a slow answer for
 * the last twin never lands on this one.
 */
export type LaunchHistoryState =
  | { state: 'loading' }
  /** `twinId` is the twin the history was read for — the page keys its route on it. */
  | { state: 'ready'; history: TwinLaunchHistoryResponse; twinId: number }
  /** `twinId` is the twin the read was asked for, so a failure is never read as another twin's. */
  | { state: 'failed'; twinId?: number };

export const useLaunchHistory = (args: {
  middlewareApi: string;
  accessToken: string | undefined;
  twinId: number | undefined;
  /** Read only while true. */
  enabled: boolean;
  /** A change of key reads again (`twin-details-page-landing`, R4). */
  readKey?: number;
}): LaunchHistoryState => {
  const { middlewareApi, accessToken, twinId, enabled, readKey } = args;
  const [read, setRead] = useState<LaunchHistoryState>({ state: 'loading' });
  const genRef = useRef(0);

  const load = useCallback(async () => {
    const gen = ++genRef.current;
    if (!accessToken || twinId === undefined) {
      setRead({ state: 'failed', twinId });
      return;
    }
    setRead({ state: 'loading' });
    // The client reports every failure as an outcome; it does not throw.
    const outcome = await getLaunchHistory(middlewareApi, accessToken, twinId);
    if (gen !== genRef.current) return;
    setRead(
      outcome.status === 'found'
        ? { state: 'ready', history: outcome.history, twinId }
        : { state: 'failed', twinId },
    );
  }, [middlewareApi, accessToken, twinId]);

  useEffect(() => {
    if (!enabled) return;
    void load();
    return () => {
      // Disabled, a change of twin or a re-read: whatever is in flight writes nothing.
      genRef.current += 1;
    };
    // `readKey` is a deliberate trigger: its value is not read, its change is the request to re-read.
  }, [enabled, load, readKey]);

  return read;
};
